import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Link, useNavigate } from 'react-router-dom';
import Router from '../../../../router';

//Utils
import { ColorsContext, useColors } from "../../../../Context/ColorsContext"
import { FormatDate } from '../../../../utils/formatDate';

//Hooks
import { useSortByColumn } from '../../../../Hooks/useSortByColumn';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import LocationSearchingOutlinedIcon from '@mui/icons-material/LocationSearchingOutlined';
import LocationDisabledOutlinedIcon from '@mui/icons-material/LocationDisabledOutlined';
import { Grid, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { LoadingProgress } from '../../../LoadingProgress';
import { useTranslation } from 'react-i18next'
import { Cell, CustomTable, CustomTableHead, CustomToolbar, Forms, HeaderCell, TableContainer, TableContent, TableHeight } from '../../../../pages/styles';

import {
  CustomSave,
  GroupActions,
} from './styles'
import { AddToQueue, Delete, Edit, ArrowUpward, ArrowDownward, AddCircleOutlineRounded } from '@mui/icons-material';
import { CustomIcon } from '../../../../layouts/Sidebar/styles';
import { CustomButtonV2, CustomIconButtonDemo } from '../../../../pages/styles';

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../Context/PermissionsContext'

function getCheck(props) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { colors } = useColors();
  if (props === true) return <CheckCircleOutlineOutlinedIcon sx={{ color: colors.grayFont }} fontSize="medium" />
}

function getUrlPlot(props) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { colors } = useColors();
  if (props === '') return <LocationDisabledOutlinedIcon fontSize="inherit" sx={{ color: colors.white }} />
  if (props != '') return <LocationSearchingOutlinedIcon fontSize="inherit" sx={{ color: colors.white }} />
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation();
  const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);
  const [headCells, setHeadCells] = React.useState([]);

  React.useEffect(() => {
    returnHeaders();
  }, [])

  const returnHeaders = () => {
    let heads = [
      {
        id: 'id',
        center: false,
        disablePadding: false,
        sortArrow: true,
        label: 'workflowManagement.table.number',
        sticky: true,
        direction: 'left',
      },
      {
        id: 'name',
        center: false,
        disablePadding: true,
        sortArrow: true,
        label: 'workflowManagement.table.name'
      },
      {
        id: 'prefix',
        center: false,
        disablePadding: true,
        sortArrow: true,
        label: 'workflowManagement.table.prefix'
      },
      {
        id: 'icon',
        center: false,
        disablePadding: true,
        sortArrow: true,
        label: 'workflowManagement.table.icon'
      },
      {
        id: 'integrationKey',
        center: false,
        disablePadding: true,
        sortArrow: true,
        label: 'workflowManagement.table.integrationKey'
      },
      {
        id: 'lastUpdated',
        center: false,
        disablePadding: true,
        sortArrow: true,
        label: 'workflowManagement.table.lastUpdate',
        customSort: 'updatedAt',
      }
    ];

    if (ability.can("UpdateOrderWorkflow")) {
      heads.splice(1, 0, addOrderRow())
    }
    if (ability.can("WorkflowStatusList") || ability.can("WorkflowStatusStandardList") || ability.can("UpdateWorkflow") || ability.can("DeleteWorkflow")) {
      heads.push(addActionsRow())
    }

    setHeadCells(heads);
  }

  function addOrderRow() {
    return {
      id: 'change_order',
      center: false,
      disablePadding: true,
      sortArrow: false,
      label: 'workflowManagement.table.changeOrder'
    }
  }

  function addActionsRow() {
    return {
      id: 'actions',
      center: true,
      disablePadding: true,
      sortArrow: false,
      label: 'common.actions.title',
      sticky: true,
      direction: 'right',
    }
  }

  const createSortHandler = (property, customSort) => (event) => {
    onRequestSort(event, property, customSort);
  };

  return (
    <CustomTableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <HeaderCell
            key={headCell.id}
            align={headCell.center ? 'center' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            dataHead={headCell}
          >
            {headCell.sortArrow == true &&
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id, headCell.customSort)}
              >
                {t(headCell.label) || headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span">
                    {order === 'desc' ? '' : ''}
                  </Box>
                ) : null}
              </TableSortLabel>
            }
            {headCell.sortArrow == false &&
              <Box component="span">
                {t(headCell.label) || headCell.label}
              </Box>
            }
          </HeaderCell>
        ))}
      </TableRow>
    </CustomTableHead >
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

function EnhancedTableToolbar(props) {
  const { t } = useTranslation();
  const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);

  return (
    <CustomToolbar>
      <Forms onSubmit={props.handleDatesFilter} isManagement={true}
        style={{ justifyContent: 'flex-start', flexWrap: 'wrap', width: '100%' }}>
          <Grid container
            spacing={1}
            alignItems="center"
            columns={{ xs: 12, sm: 12, md: 16, lg: 12, xl: 10 }}
          >
            <Grid item xs={6} sm={3} lg={2} xl={1}>
              {ability.can("CreateWorkflow") &&
                <CustomButtonV2
                  variant="contained"
                  onClick={props.handleCreateWorkflow}
                  startIcon={<AddCircleOutlineRounded />}
                >
                  {t("workflowManagement.table.createNew")}
                </CustomButtonV2>
              }
            </Grid>
            <Grid item xs={6} sm={3} lg={2} xl={1}>
              {ability.can("CreateWorkflow") &&
                <CustomButtonV2
                  variant="contained"
                  startIcon={<CustomSave/>}
                  aria-label="save orders"
                  onClick={props.handleSaveOrder}
                  btnType="primary"
                >
                  {t('workflowManagement.table.saveOrder')}
                </CustomButtonV2>
              }
            </Grid>
          </Grid>
      </Forms>
    </CustomToolbar>
  );
}

EnhancedTableToolbar.propTypes = {
};


export default function TableWorkflowManagement({
  filteredRequest,
  rows,
  loading,
  handleCreateWorkflow,
  handleDelete,
  handleOrderUp,
  handleOrderDown,
  handleSaveOrder
}) {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { format } = new FormatDate();
  const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);
  const { order, orderBy, getComparator, stableSort, handleRequestSort } = useSortByColumn('updatedAt');

  const theme = useTheme();
  const isMobileOrBigger = useMediaQuery(theme.breakpoints.up('sm'));

  const [page, setPage] = React.useState(0);
  const [dense,] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <EnhancedTableToolbar
        handleCreateWorkflow={handleCreateWorkflow}
        handleSaveOrder={handleSaveOrder}
      />
      <TableContainer sx={{ width: '100%' }}>
        {loading && <LoadingProgress />}
        {!loading &&
          <React.Fragment>
            <TableHeight isManagement={isMobileOrBigger ?? false}>
              <CustomTable
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  sx={{ minWidth: 750 }}
                  stickyHeader
                />
                <TableContent>
                  {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                      rows.sort(getComparator(order, orderBy)).slice() */}
                  {stableSort(filteredRequest, getComparator())
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                        >
                          <Cell direction='left' sx={{ zIndex: 1 }}>{row.id}</Cell>

                          {ability.can("UpdateOrderWorkflow") &&
                            <Cell>
                              <Grid container spacing={1}>
                                <Grid item>
                                  <Tooltip title={t('workflowManagement.table.moveUp')}>
                                    <span>
                                      <CustomIconButtonDemo
                                        btnType=""
                                        size="small"
                                        aria-label="up"
                                        disabled={index != 0 ? false : true}
                                        onClick={() => handleOrderUp(index, row.order)}
                                      >
                                        <ArrowUpward />
                                      </CustomIconButtonDemo>
                                    </span>
                                  </Tooltip>
                                </Grid>
                                <Grid item>
                                  <Tooltip title={t('workflowManagement.table.moveDown')}>
                                    <span>
                                      <CustomIconButtonDemo
                                        btnType=""
                                        size="small"
                                        aria-label="down"
                                        disabled={index != rows.length - 1 ? false : true}
                                        onClick={() => handleOrderDown(index, row.order)}
                                      >
                                        <ArrowDownward />
                                      </CustomIconButtonDemo>
                                    </span>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </Cell>
                          }
                 
                          <Cell component="th" scope="row">
                            {row.name}
                          </Cell>
                          <Cell>{row.prefix}</Cell>
                          <Cell>
                            <CustomIcon baseClassName="material-icons-outlined">{row.icon || ''}</CustomIcon>
                          </Cell>
                          <Cell component="th" scope="row">
                            {row.integrationKey}
                          </Cell>
                          <Cell>
                            { format({ date: row?.updatedAt, format: true }) }
                          </Cell>
                          <Cell direction='right'>
                            <GroupActions>
                              {(ability.can("WorkflowStatusList") || ability.can("WorkflowStatusStandardList")) &&
                                <Grid item>
                                  {/* boton agregar estado*/}
                                  <Tooltip title={t('workflowManagement.table.addFlowState')}>
                                    <CustomIconButtonDemo
                                      btnType=""
                                      size="small"
                                      aria-label="add status"
                                      onClick={() => navigate(Router.appWorkflowStatus.replace(':id', row.id))}
                                    >
                                      <AddToQueue />
                                    </CustomIconButtonDemo>
                                  </Tooltip>
                                </Grid>
                              }
                              {ability.can("UpdateWorkflow") &&
                                <Grid item>
                                  {/* boton edit  */}
                                  <Tooltip title={t('workflowManagement.table.editFlow')}>
                                    <Link to={Router.appWorkflowManagementDetail} state={{ row: row }}>
                                      <CustomIconButtonDemo
                                        btnType=""
                                        size="small"
                                        aria-label="detail"
                                      >
                                        <Edit />
                                      </CustomIconButtonDemo>
                                    </Link>
                                  </Tooltip>
                                </Grid>
                              }
                              {ability.can("DeleteWorkflow") &&
                                <Grid item>
                                  {/* boton delete  */}
                                  <Tooltip title={t('workflowManagement.table.deleteFlow')}>
                                    <CustomIconButtonDemo
                                      btnType="delete"
                                      size="small"
                                      aria-label="delete"
                                      onClick={() => handleDelete(row)}
                                    >
                                      <Delete />
                                    </CustomIconButtonDemo>
                                  </Tooltip>
                                </Grid>
                              }
                            </GroupActions>
                          </Cell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableContent>
              </CustomTable>
            </TableHeight>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('common.itemsPerPage')}
              labelDisplayedRows={
                ({ from, to, count }) => {
                  return '' + from + '-' + to + ` ${t('common.of')} ` + count
                }
              }
              sx={{
                borderTop: '1px solid ' + colors.gray,
              }}
            />
          </React.Fragment>
        }
      </TableContainer>
      {/*
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
        */}
    </Box>
  );
}

